import React, { CSSProperties } from 'react';
import styled from 'styled-components';
import {
  Grid,
  Select,
  InputLabel,
  TableCell,
  SelectProps,
  Table,
  Box,
} from '@mui/material';
import { withStyles, makeStyles } from 'tss-react/mui';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { omit } from 'ramda';
import { Row, SPWInput } from 'spw-shared/globalComponents';
import CardAtom from 'spw-components/atomic/atoms/card';
import { colors } from 'spw-theme';
import { Link } from 'react-router-dom-v5';

export const ExportedCardHeaderContainer = styled.div`
  padding: 8px 0px 5px 0px;
  font-family: Roboto;
  font-style: normal;
  font-size: 10px;
  line-height: 12px;
  letter-spacing: 0.25px;
  text-transform: uppercase;
  display: flex;
  border-bottom: 1px solid #dfdfdf;

  &.mb {
    margin-bottom: 10px;
  }
`;

export const NameBoxNoWrap = styled(Box)`
  margin-left: 5px;
  white-space: nowrap;
`;

export const ProgramLabel = styled(Box)`
  margin-left: 32px;
  font-weight: bold;
`;

export const DepartmentLabel = styled(ProgramLabel)``;

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;

  &.main-container {
    margin: 0px 24px;
  }
`;

export const ControlsContainer = styled(Container)`
  flex-direction: row;
  justify-content: space-between;
  margin: 8px 0 24px;
`;

export const HeadcountSeatcountControlsRow = styled(Row)`
  > div:not(:first-child) {
    margin-left: 24px;
  }
`;

export const Header = styled.h1`
  font-family: 'Roboto';
  font-size: 32px;
  font-style: normal;
  font-weight: 300;
  line-height: 34px;
  letter-spacing: 0em;
  text-align: left;
  margin: 0px 0px 24px 0px;
`;

export const CardsContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-between;
`;

export const Card = styled(CardAtom)`
  display: flex;
  flex-direction: column;
  flex: 1 1 336px;
  box-sizing: border-box;
  max-width: 336px;
  margin-bottom: 16px;
`;

export const CardTitle = styled.div`
  font-family: Roboto;
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  min-height: 24px;
  line-height: 22px;
  text-align: center;
`;

export const CardSubtitle = styled.div`
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 20px;
  margin: 8px 0;
  text-align: center;
`;

export const CardRequirementHint = styled.div`
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  display: flex;
  justify-content: center;
  color: ${colors.mainGray};
`;

export const CardList = styled.ul`
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  margin: 16px 0 24px 0;
  padding-inline-start: 16px;
`;

export const ClickableBreadcrumb = styled(Link)`
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 400;
  font-size: 18px;
  line-height: 20px;
  color: ${colors.mainGray};
  cursor: pointer;
  margin: 0;
`;

export const BreadCrumb = styled.h2`
  font-size: 18px;
  font-weight: 500;
  font-size: 18px;
  line-height: 20px;
  color: ${colors.dark};
  margin-top: 0px;
  margin-bottom: 0px;
`;

export const BreadCrumbRow = styled(Row)`
  align-items: center;
  justify-content: space-between;

  .save-button {
    padding: 10px 16px;

    line-height: 16px;
    letter-spacing: 0.56px;
  }
`;

export const Content = styled.div`
  display: flex;
  flex-direction: column;
  margin: 22px 0 16px;

  .MuiPaper-root {
    padding: 8px 16px 17px;
    border-radius: 4px;
    border: 1px solid ${colors.veryLightGray};
  }
`;

export const FormTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  line-height: 24px;
  margin-bottom: 8px;

  &.table-header {
    margin-bottom: 0;
    padding: 4px 0;
  }

  display: flex;
  justify-content: space-between;
  align-items: center;

  .MuiFormControlLabel-label {
    font-size: 12px;
    line-height: 16px;
  }
`;

interface CustomSelectProps {
  label: string;
  forceShrink?: boolean;
}

export const CustomSelect = withStyles(
  (props: SelectProps & CustomSelectProps) => {
    const labelStyle: CSSProperties = { background: 'white' };

    if (props.forceShrink) {
      labelStyle.transform = 'translate(14px, -6px) scale(0.75)';
    }

    return (
      <>
        <InputLabel style={labelStyle}>{props.label}</InputLabel>
        <Select
          variant="outlined"
          {...(omit(['label', 'forceShrink'], props) as SelectProps &
            CustomSelectProps)}
          IconComponent={KeyboardArrowDownIcon}
          MenuProps={{
            disableScrollLock: true,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
          }}
        />
      </>
    );
  },
  {
    root: {
      padding: '13px 1px 9px 13px',
      '&& .MuiSelect-select': {
        padding: 0,
        borderRadius: '4px',
        fontSize: '14px',
        background: 'white',
        lineHeight: 1.8,
        width: '100%',
        height: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        zIndex: 0,
      },
    },
    select: {
      '&:focus': {
        background: 'none',
        borderRadius: '4px',
      },
    },
  }
);

const useStyles = makeStyles()({
  paper: {
    '& ul li': {
      fontSize: '14px',
      '& > strong': {
        fontSize: '14px',
      },
    },
  },
});

export const DepartmentSelect = withStyles(
  (props: SelectProps & CustomSelectProps) => {
    const { classes } = useStyles();

    return (
      <>
        <InputLabel
          style={{ background: 'white' }}
          id="departments-select-label"
        >
          {props.label}
        </InputLabel>
        <Select
          labelId="departments-select-label"
          variant="outlined"
          {...(omit(['label'], props) as Omit<
            SelectProps & CustomSelectProps,
            'label'
          >)}
          IconComponent={KeyboardArrowDownIcon}
          MenuProps={{
            PaperProps: {
              className: classes.paper,
            },
            disableScrollLock: true,
          }}
        />
      </>
    );
  },
  {
    root: {
      '&& .MuiSelect-select': {
        borderRadius: '4px',
        border: `1px solid ${colors.selectBorder}`,
        padding: '12px 15px 8px 12px',
        fontSize: '14px',
        background: 'white',
        lineHeight: 1.8,
        zIndex: 0,
      },
    },
    select: {
      '&:focus': {
        border: `1px solid ${colors.selectBorder}`,
        background: 'none',
        borderRadius: '4px',
      },
    },
  }
);

export const FactorSection = styled.h4`
  font-family: Roboto;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 16px;
  margin-block: 0;
`;

export const FactorTitle = styled.span`
  font-family: Roboto;
  font-size: 10px;
  font-style: normal;
  font-weight: 700;
  line-height: 14px;
  letter-spacing: 0.25px;
  text-align: left;
`;

export const BigNumber = styled.div`
  && {
    font-family: Roboto;
    font-size: 18px;
    line-height: 27px;
    font-style: normal;
    font-weight: 400;
    letter-spacing: 0em;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    white-space: nowrap;
  }

  &.large {
    width: 90px;
  }
`;

export const Label = styled.span`
  color: rgba(97, 97, 101, 0.87);
  font-family: Roboto;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.4px;
  text-align: left;
  white-space: pre;
`;

export const HeadcountInput = styled(SPWInput)`
  && {
    width: 62px;
    min-width: 62px;
    height: 40px;
    display: inline-flex;

    &.with-end-adornment {
      margin-right: 13px;
    }

    &.growth-rate {
      width: 75px;
      min-width: 75px;
      margin-right: 0px;
    }

    &.no-offset .MuiOutlinedInput-adornedEnd {
      margin-left: 0;
    }

    &.highlight .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline {
      border: 2px solid ${colors.brandBlue};
    }

    .MuiInputBase-input {
      padding-left: 20px;
      background: ${colors.white};
    }

    .MuiOutlinedInput-input {
      height: 40px;
      font-size: 14px;
      line-height: 16px;
      text-align: right;
      padding: 0px 12px 0px 0px;
      border-radius: 4px;
    }

    .MuiOutlinedInput-adornedStart {
      width: 63px;
      margin-left: unset;
    }

    .MuiInputBase-adornedEnd {
      width: 63px;
      margin-right: 6px;
      padding-right: 8px;

      .MuiTypography-colorTextSecondary {
        color: currentColor;
      }

      input {
        padding-left: 6px;
        padding-right: 4px;
        text-align: right;
      }

      .MuiInputAdornment-positionEnd {
        margin-left: 4px;
      }
    }

    .MuiInputBase-adornedStart {
      padding-left: 8px;

      input {
        padding-right: 8px;
      }
    }
  }

  &.percentage {
    .MuiOutlinedInput-input {
      padding: 0px;
    }
  }

  &.base {
    width: 89px;
    min-width: 89px;
  }

  &.floating-label {
    .MuiInputLabel-outlined.MuiInputLabel-shrink {
      transform: translate(14px, -14px) scale(0.75);
    }

    fieldset.MuiOutlinedInput-notchedOutline {
      border-radius: 4px;
      top: 0;

      legend {
        display: none;
      }
    }
  }
`;

export const GrowthTableHeadcountInput = styled(HeadcountInput)`
  && .MuiOutlinedInput-input {
    padding: 0;
    text-align: center;
  }
`;

export const Ratio = styled.span`
  font-size: 14px;
`;

export const Operator = styled.div`
  display: flex;
  width: 100%;
  height: 40px;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  &.equal-sign {
    margin-right: 8px;
  }
`;

export const FactorContainer = styled(Grid)`
  && {
    margin: 8px 0px;
    border-bottom: 1px solid #e0e0e0;

    &.hidden-border {
      border: 0;
    }

    &.mb-0 {
      margin-bottom: 0px;
    }

    &.pb-0 {
      padding-bottom: 0px;
    }
  }
`;

export const SeatCountFactorContainer = styled(FactorContainer)`
  && {
    padding-bottom: 20px;
    box-sizing: content-box;
    margin: 0 0 19px 0;
  }
`;

export const TargetUsfContainer = styled(SeatCountFactorContainer)`
  ${Operator} {
    margin-left: 8px;
  }
`;

export const TooltipTitle = styled.h2`
  font-family: Roboto;
  font-style: normal;
  font-weight: 500;
  font-size: 18px;
  line-height: 24px;
  margin: 16px 0;
`;

export const TooltipSection = styled.h3`
  font-family: Roboto;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  text-transform: uppercase;
  margin: 8px 0px 0px 0px;
`;

export const TooltipText = styled.p`
  font-weight: normal;
  font-size: 14px;
  line-height: 20px;
  margin: 0px;

  &.secondary {
    color: ${colors.mainGray};
  }
`;

export const TooltipBody = styled.div`
  > *:not(:first-child) {
    margin: 8px 0 0;
  }
`;

export const EmptyList = styled.p`
  font-style: italic;
  font-weight: normal;
  font-size: 14px;
  line-height: 16px;
  letter-spacing: 0.25px;
  color: ${colors.black};
`;

export const RowTitle = styled.p`
  font-weight: 400;
  letter-spacing: 0em;
  text-align: left;
  margin: 0px;
  font-size: 14px;
  line-height: 20px;
`;

export const RowTitleDepartment = styled(RowTitle)`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

export const RowSmall = styled.p`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  text-align: left;
  margin: 0px;
`;

export const NumberOp = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

export const CalculatorTable = styled(Table)`
  thead {
    th {
      &:first-child {
        width: 157px;
      }

      &:nth-child(n + 3) {
        width: 67px;
      }
    }
  }

  tbody {
    tr {
      &:first-child td {
        padding: 14px 16px 14px;

        &:nth-child(2) {
          padding: 6px 2px;
        }
      }

      td:first-child:not([colspan='2']) {
        width: 153px;
      }
    }
  }
`;

export const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

export const HeaderCell = withStyles(TableCell, {
  root: {
    fontFamily: 'Roboto',
    fontStyle: 'normal',
    fontWeight: 700,
    fontSize: '10px',
    lineHeight: '14px',
    letterSpacing: '0.25px',
    padding: '11px 0 6px',
    cursor: 'pointer',
    minWidth: '60px',
  },
});

export const GrowthRateHeaderCell = styled(HeaderCell)`
  width: 85px;
`;

export const TitleHeaderCell = styled(HeaderCell)`
  && {
    font-size: 14px;
    font-weight: 500;
    line-height: 24px;
    padding: 0;
    letter-spacing: 0;
  }
`;

export const BodyCell = styled(TableCell)`
  && {
    padding: 6px 2px;
    font-size: 14px;
    font-style: normal;
    line-height: 16px;
    letter-spacing: 0em;
    cursor: pointer;
  }
`;

export const GrowthRateCell = styled(BodyCell)`
  && {
    padding: 6px 0;
    text-align: left;

    .MuiInputBase-root {
      width: unset;
      margin-right: 0;
    }
  }
`;

export const CalculatorValue = styled.div`
  && {
    text-align: center;
    font-weight: normal;
  }

  &.current {
    min-width: 57px;
  }

  &.total {
    font-weight: 500;
  }
`;

export const PaddedGrid = styled(Grid)`
  && {
    padding: 4px;
  }
`;

export const CalculatorTipsContainer = styled(Row)`
  margin-top: 8px;
  font-size: 16px;
  line-height: 20px;
  color: ${colors.mainGray};
  padding-left: 16px;

  > :first-child {
    margin-right: 6px;
  }
`;

export const HeadcountContainer = styled.div`
  flex: 1;
`;

export const Divider = styled.div`
  width: 1px;
  height: 78px;
  border-right: 1px solid ${colors.grayDivider};

  margin: 0 16px;
`;
